import React, { useState, useEffect, useMemo } from "react";
import styles from "./LevelsTab.module.scss";
import {
  MdBlock,
  MdVisibility,
  MdEdit,
  MdDelete,
  MdSettings,
  MdAdd,
  MdSave,
  MdFlashOn,
} from "react-icons/md";
import Axios from "../../../../_services/caller.service";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const PROTECTED_LEVELS = ["support1", "support2"];

const LevelsTab = ({ role }) => {
  const { t } = useTranslation();
  const [levels, setLevels] = useState([]);
  const [selectedLevelId, setSelectedLevelId] = useState("");
  const [permissionsData, setPermissionsData] = useState([]);
  const [newLevelName, setNewLevelName] = useState("");
  const [renameLevelName, setRenameLevelName] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [modsRes, lvlsRes] = await Promise.all([
          Axios.get("/getAllModulesWithActions?role=" + encodeURIComponent(role)),
          Axios.get("/getAllLevelsWithAccess?role=" + encodeURIComponent(role)),
        ]);
        setPermissionsData(modsRes.data);
        setLevels(lvlsRes.data);
        if (lvlsRes.data.length) {
          setSelectedLevelId(lvlsRes.data[0].id);
          setRenameLevelName(lvlsRes.data[0].name);
        }
      } catch (err) {
        console.error(err);
        toast.error(t("levels.loading_error"));
      }
    })();
  }, [role]);

  const selectedLevel = useMemo(
    () => levels.find((l) => l.id === selectedLevelId),
    [levels, selectedLevelId]
  );

  useEffect(() => {
    if (selectedLevel) {
      setRenameLevelName(selectedLevel.name);
    }
  }, [selectedLevel]);

  const updatePermission = async (actionKey, value) => {
    if (!selectedLevel) return;

    const updatedLevels = levels.map((lvl) =>
      lvl.id === selectedLevelId
        ? { ...lvl, permissions: { ...lvl.permissions, [actionKey]: value } }
        : lvl
    );
    setLevels(updatedLevels);

    try {
      const payload = updatedLevels.find((l) => l.id === selectedLevelId);
      await Axios.post("/saveSupportLevelActions", payload);
      toast.success(t("levels.permission_updated"));
    } catch (err) {
      console.error(err);
      toast.error(t("levels.permission_update_error"));
    }
  };

  const handleAddLevel = async () => {
    if (!newLevelName.trim()) {
      toast.error(t("levels.name_empty"));
      return;
    }
    try {
      const res = await Axios.post(
        "/createSupportLevelMeta?role=" + encodeURIComponent(role),
        { name: newLevelName.trim() }
      );
      const created = {
        id: res.data.id,
        name: res.data.name,
        role,
        permissions: permissionsData.reduce((acc, mod) => {
          mod.actions.forEach((a) => { acc[a.key] = 0; });
          return acc;
        }, {}),
      };
      setLevels((prev) => [...prev, created]);
      setSelectedLevelId(created.id);
      setNewLevelName("");
      toast.success(t("levels.level_added"));
    } catch (err) {
      console.error("Erreur création niveau :", err);
      toast.error(t("levels.level_creation_error"));
    }
  };

  const handleRenameLevel = async () => {
    if (!selectedLevel) return;
    if (!renameLevelName.trim()) {
      toast.error(t("levels.new_name_empty"));
      return;
    }
    try {
      await Axios.put("/renameSupportLevel/" + selectedLevelId, {
        name: renameLevelName.trim(),
      });
      setLevels((prev) =>
        prev.map((lvl) =>
          lvl.id === selectedLevelId ? { ...lvl, name: renameLevelName.trim() } : lvl
        )
      );
      toast.success(t("levels.level_renamed"));
    } catch (err) {
      console.error("Erreur renommage niveau :", err);
      toast.error(t("levels.rename_error"));
    }
  };

  const handleDeleteLevel = async () => {
    if (!selectedLevel) return;
    if (PROTECTED_LEVELS.includes(selectedLevel.name)) {
      toast.error(t("levels.protected_level"));
      return;
    }
    try {
      await Axios.delete("/deleteSupportLevel/" + selectedLevelId);
      setLevels((prev) => prev.filter((lvl) => lvl.id !== selectedLevelId));
      setTimeout(() => {
        setSelectedLevelId((prev) => {
          const remaining = levels.filter((lvl) => lvl.id !== prev);
          return remaining.length ? remaining[0].id : "";
        });
      }, 0);
      toast.success(t("levels.level_deleted"));
    } catch (err) {
      console.error("Erreur suppression niveau :", err);
      toast.error(t("levels.delete_error"));
    }
  };

  if (!selectedLevel) {
    return <div className={styles.emptyState}>{t("levels.no_level_found")}</div>;
  }

  return (
    <div className={styles.container}>
      {/* Sélecteur de niveau + bouton avancé */}
      <div className={styles.controls}>
        <div className={styles.selectRow}>
          <select
            className={styles.levelSelect}
            value={selectedLevelId}
            onChange={(e) => setSelectedLevelId(Number(e.target.value))}
          >
            {levels.map((lvl) => (
              <option key={lvl.id} value={lvl.id}>
                {lvl.name}
              </option>
            ))}
          </select>

          <button
            className={styles.btnAdvanced}
            onClick={() => setShowAdvanced((v) => !v)}
          >
            <MdSettings /> {t("levels.advanced_settings")}
          </button>
        </div>
      </div>

      {/* Section avancée */}
      {showAdvanced && (
        <div className={styles.advancedSection}>
          <div className={styles.advancedRow}>
            <input
              type="text"
              placeholder={t("levels.new_level_name")}
              value={newLevelName}
              onChange={(e) => setNewLevelName(e.target.value)}
            />
            <button className={styles.btnSuccess} onClick={handleAddLevel}>
              <MdAdd /> {t("levels.add")}
            </button>
          </div>
          <div className={styles.advancedRow}>
            <input
              type="text"
              placeholder={t("levels.rename_level")}
              value={renameLevelName}
              onChange={(e) => setRenameLevelName(e.target.value)}
            />
            <button className={styles.btnPrimary} onClick={handleRenameLevel}>
              <MdSave /> {t("levels.rename")}
            </button>
          </div>
          <div className={styles.advancedRow}>
            <button
              className={styles.btnDanger}
              onClick={handleDeleteLevel}
              disabled={PROTECTED_LEVELS.includes(selectedLevel.name)}
            >
              <MdDelete /> {t("levels.delete_level")}
            </button>
          </div>
        </div>
      )}

      {/* Légende */}
      <div className={styles.legend}>
        <div className={styles.legendItem}>
          <MdBlock className={styles.badgeNone} /> {t("levels.legend_none")}
        </div>
        <div className={styles.legendItem}>
          <MdEdit className={styles.badgeFull} /> {t("levels.legend_read_write")}
        </div>
        <div className={styles.legendItem}>
          <MdVisibility className={styles.badgeRead} /> {t("levels.legend_read")}
        </div>
      </div>

      {/* Modules & permissions */}
      {permissionsData.map((mod) => (
        <div key={mod.id} className={styles.moduleCard}>
          <h3>{mod.name}</h3>
          <table className={styles.permissionsTable}>
            <thead>
              <tr>
                <th>{t("support.action_header")}</th>
                <th>{t("support.description_header")}</th>
                <th className="has-tooltip" aria-label={t("support.effective_permission")}><MdFlashOn className={styles.badgeEffective} /></th>
                {[0, 2, 1].map((val) => (
                  <th
                    key={val}
                    className="has-tooltip"
                    aria-label={val === 0 ? t("support.no_access") : val === 2 ? t("support.read_write") : t("support.read_only")}
                  >
                    {val === 0 && <MdBlock className={styles.badgeNone} />}
                    {val === 2 && <MdEdit className={styles.badgeFull} />}
                    {val === 1 && <MdVisibility className={styles.badgeRead} />}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {mod.actions.map((action) => {
                const current = selectedLevel.permissions[action.key] ?? 0;
                return (
                  <tr key={action.key}>
                    <td className={styles.actionKeyCell}>{action.key}</td>
                    <td className={styles.descCell}>{action.description || "-"}</td>
                    <td className={styles.previewCell}>
                      {current === 0 && <MdBlock className={styles.badgeNone} />}
                      {current === 2 && <MdEdit className={styles.badgeFull} />}
                      {current === 1 && <MdVisibility className={styles.badgeRead} />}
                    </td>
                    {[0, 2, 1].map((val) => (
                      <td key={val}>
                        <input
                          type="checkbox"
                          checked={current === val}
                          onChange={() => updatePermission(action.key, val)}
                        />
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
};

export default LevelsTab;
